import { useParams, Link, useNavigate } from 'react-router-dom';
import './WikiItemDetail.css';
import { FadeIn, StaggerContainer, StaggerItem } from '../components/Animations';
import { CraftingGrid, McIcon, slotsToArray } from '../components/CraftingGrid';
import { McText } from './MinecraftColors';

const VIDEO = "https://pub-332780ae20b540038a9ea772f1bf747c.r2.dev/background_vids/vid_1.mp4";

const rarityColors = {
  common: '#9e9e9e',
  uncommon: '#52c078',
  rare: '#5a9ee0',
  epic: '#a070e0',
  legendary: '#f0ab20',
  unique: '#d94f4f',
  boss: '#e84c3d',
};

const calculateOneInAAmount = (chance) => {
  if (!chance || chance <= 0) return null;
  if (chance >= 100) return '1 in 1';
  const amount = Math.round(100 / chance);
  return `1 in ${amount}`;
}

function itemMaterial(item) {
  return item?.material || item?.type || 'barrier';
}

// Resolve a slot key → custom item by slug or display name, null for vanilla keys
function resolveCustomItem(key, items) {
  if (!key) return null;
  const lower = key.toLowerCase();
  return items.find(i => i.slug === key || i.name?.toLowerCase() === lower) ?? null;
}


function ObtainCard({ label, iconKey, children }) {
  return (
    <div className="wiki-obtain-drop-card">
      <div className="wiki-obtain-drop-head">
        <McIcon materialKey={iconKey} size={20} />
        <span className="wiki-obtain-drop-label">{label}</span>
        <div className="wiki-obtain-drop-rule" />
      </div>
      {children}
    </div>
  );
}

function ObtainEntry({ entry, mobs, material }) {
  if (entry.type === 'drop') {
    const mob = entry.mob_id ? mobs.find(m => String(m.id) === String(entry.mob_id)) : null;
    const mobLabel = mob
      ? <Link to={`/wiki/mob/${mob.slug}`} className="wiki-obtain-mob-link">{mob.name}</Link>
      : entry.mob_name
        ? <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--t-sm)' }}>{entry.mob_name}</span>
        : <span className="wiki-obtain-mob-unknown">Unknown mob</span>;
    return (
      <ObtainCard label="Mob Drop" iconKey={material}>
        <div className="wiki-obtain-drop-row">
          {mobLabel}
          {entry.chance
            ? <span className="wiki-obtain-chance">{entry.chance}% ({calculateOneInAAmount(entry.chance)})</span>
            : <span className="wiki-obtain-chance-unknown">—</span>
          }
        </div>
      </ObtainCard>
    );
  }

  if (entry.type === 'block_drop') {
    return (
      <ObtainCard label="Block Drop" iconKey="grass_block">
        <div className="wiki-obtain-drop-row">
          <Link
            to={`/wiki/blocks?q=${encodeURIComponent(entry.location || '')}`}
            className="wiki-obtain-mob-link"
            style={{ color: 'var(--text-secondary)', fontSize: 'var(--t-sm)' }}
          >
            {entry.location || 'Unknown block'}
          </Link>
          {entry.chance
            ? <span className="wiki-obtain-chance">{entry.chance}% ({calculateOneInAAmount(entry.chance)})</span>
            : <span className="wiki-obtain-chance-unknown">—</span>
          }
        </div>
      </ObtainCard>
    );
  }

  if (entry.type === 'supply_drop') {
    return (
      <ObtainCard label="Supply Drop" iconKey="chest">
        <div className="wiki-obtain-drop-row">
          <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--t-sm)' }}>
            {entry.location || 'Supply Drop'}
          </span>
          {entry.chance
            ? <span className="wiki-obtain-chance">{entry.chance}% ({calculateOneInAAmount(entry.chance)})</span>
            : <span className="wiki-obtain-chance-unknown">—</span>
          }
        </div>
        <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid var(--border)' }}>
          <Link
            to="/wiki/supply-drops"
            style={{ fontSize: 'var(--t-xs)', color: 'var(--teal)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}
          >
            See all drop chances by rarity →
          </Link>
        </div>
      </ObtainCard>
    );
  }

  if (entry.type === 'treasure') {
    return (
      <ObtainCard label="Treasure" iconKey="golden_sword">
        <div className="wiki-obtain-drop-row">
          <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--t-sm)' }}>
            {entry.location || 'Treasure'}
          </span>
          {entry.chance
            ? <span className="wiki-obtain-chance">{entry.chance}% ({calculateOneInAAmount(entry.chance)})</span>
            : <span className="wiki-obtain-chance-unknown">—</span>
          }
        </div>
      </ObtainCard>
    );
  }

  // Simple row types
  let text;
  if (entry.type === 'craft') text = 'Craftable at crafting table';
  else text = entry.description;

  return (
    <div className="wiki-obtain-row">
      <span className="wiki-obtain-bullet">▸</span>
      <span>{text}</span>
    </div>
  );
}

function parseObtain(raw) {
  try { return JSON.parse(raw || '[]'); } catch { return []; }
}

function parseHistory(raw) {
  try { return JSON.parse(raw || '[]'); } catch { return []; }
}

function formatChance(raw) {
  if (!raw && raw !== 0) return null;
  const num = parseFloat(String(raw).replace('%', ''));
  if (isNaN(num) || num <= 0) return String(raw);
  if (num >= 100) return `${num}% (1/1)`;
  return `${num}% (1/${Math.round(100 / num)})`;
}

export default function WikiItemDetail({ items = [], mobs = [], sets = [] }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const item = items.find(i => i.slug === slug && i.enabled !== 0 && i.enabled !== '0');

  if (!item) {
    return (
      <div className="container" style={{ padding: '4rem 2rem', textAlign: 'center' }}>
        <h2>Item not found</h2>
        <Link to="/wiki" className="btn btn-secondary" style={{ marginTop: '1rem' }}>Back to Wiki</Link>
      </div>
    );
  }

  return (
    <div className="wiki-detail-root">
      <div className="wiki-detail-hero">
        <video className="wiki-detail-hero-vid" src={VIDEO} autoPlay muted loop playsInline />
        <div className="wiki-detail-hero-overlay" />
        <FadeIn className="wiki-detail-hero-content container" y={30} duration={0.8}>
          <button className="wiki-back-btn" onClick={() => navigate(-1)}>← Back</button>
          <div className="wiki-detail-title-section">
            <div className="wiki-detail-icon-box">
              <McIcon materialKey={itemMaterial(item)} size={48} />
            </div>
            <div>
              <h1 className="wiki-detail-item-name">{item.name}</h1>
              <div
                className="wiki-detail-item-type"
                style={{ color: rarityColors[item.rarity] || 'var(--text-secondary)' }}
              >
                {item.rarity} {item.category}
              </div>
            </div>
          </div>
        </FadeIn>
      </div>

      <StaggerContainer className="container">
        <div className="wiki-detail-grid">
          <StaggerItem className="wiki-detail-main">
            {item.lore && (
              <div className="wiki-lore-box">
                {item.lore.split('\n').map((line, i) => (
                  <div key={i} className="wiki-lore-line">
                    {line ? <McText text={line} /> : <span>&nbsp;</span>}
                  </div>
                ))}
              </div>
            )}

            {(item.consume_delay_ms || item.obtain_method) && (
              <div>
                <span className="wiki-detail-label">Vanilla Properties</span>
                <div className="wiki-detail-box">
                  {item.consume_delay_ms && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: item.obtain_method ? '0.4rem' : 0 }}>
                      <span style={{ color: 'var(--text-muted)', fontSize: 'var(--t-sm)' }}>Consume Delay</span>
                      <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{(item.consume_delay_ms / 1000).toFixed(1)}s</span>
                    </div>
                  )}
                  {item.obtain_method && (
                    <div>
                      <span style={{ color: 'var(--text-muted)', fontSize: 'var(--t-xs)', display: 'block', marginBottom: 2 }}>How to obtain</span>
                      <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--t-sm)' }}>{item.obtain_method}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {item.abilities?.length > 0 && (
              <div>
                <span className="wiki-detail-label">Abilities</span>
                <div className="wiki-ability-list">
                  {item.abilities.map((ability, idx) => (
                    <div key={idx} className="wiki-ability-card">
                      <div className="wiki-ability-head">
                        <span className="wiki-ability-name">{ability.name}</span>
                        {ability.type && <span className="wiki-ability-type">{ability.type}</span>}
                      </div>
                      <div className="wiki-ability-desc">{ability.description}</div>
                      {ability.cooldown && (
                        <div className="wiki-ability-cooldown">Cooldown: {ability.cooldown}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {item.description && (
              <div>
                <span className="wiki-detail-label">Description</span>
                <div className="wiki-detail-box">
                  <p style={{ margin: 0, color: 'var(--text-secondary)' }}>{item.description}</p>
                </div>
              </div>
            )}
          </StaggerItem>

          <StaggerItem className="wiki-detail-sidebar">
            {/* ── Crafting Recipe ── */}
            <div>
              <span className="wiki-detail-label">Crafting Recipe</span>
              <div className="wiki-detail-box">
                {(() => {
                  const slots = slotsToArray(item.recipe);
                  const hasSlots = slots.some(s => s.key);
                  if (!hasSlots) {
                    return <p style={{ color: 'var(--text-muted)', fontSize: 'var(--t-sm)', margin: 0 }}>Non-craftable item.</p>;
                  }

                  // Aggregate ingredient totals
                  const totals = {};
                  slots.forEach(s => {
                    if (!s.key) return;
                    totals[s.key] = (totals[s.key] || 0) + (s.count || 1);
                  });

                  return (
                    <>
                      <CraftingGrid
                        slots={slots}
                        outputKey={itemMaterial(item)}
                        outputCount={item.recipe?.output_count ?? item.recipe_output_count ?? 1}
                        note={item.recipe?.note}
                        items={items}
                      />
                      <div className="wiki-recipe-ingredients">
                        <span className="wiki-recipe-ing-label">Ingredients</span>
                        {Object.entries(totals).map(([key, total]) => {
                          const custom = resolveCustomItem(key, items);
                          const name = custom ? custom.name : key.replace(/_/g, ' ');
                          return (
                            <div key={key} className="wiki-recipe-ing-row">
                              <McIcon materialKey={custom?.material || key} size={18} />
                              {custom ? (
                                <Link
                                  to={`/wiki/item/${custom.slug}`}
                                  className="wiki-recipe-ing-name wiki-recipe-ing-link"
                                >
                                  {name}
                                </Link>
                              ) : (
                                <span className="wiki-recipe-ing-name">{name}</span>
                              )}
                              <span className="wiki-recipe-ing-qty">×{total}</span>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* ── Furnace Recipe ── */}
            {item.furnace_recipe && (() => {
              let fr;
              try { fr = typeof item.furnace_recipe === 'string' ? JSON.parse(item.furnace_recipe) : item.furnace_recipe; } catch { return null; }
              if (!fr || !fr.ingredient) return null;
              const ingItem = resolveCustomItem(fr.ingredient, items);
              const ingMat = ingItem ? itemMaterial(ingItem) : fr.ingredient;
              const ingName = ingItem ? ingItem.name : (fr.ingredient || '').replace(/_/g, ' ');
              const fuelItem = resolveCustomItem(fr.fuel, items);
              const fuelMat = fuelItem ? itemMaterial(fuelItem) : (fr.fuel || '').toLowerCase().replace(/\s+/g, '_');
              const fuelName = fuelItem ? fuelItem.name : (fr.fuel ? fr.fuel.replace(/_/g, ' ') : null);
              return (
                <div>
                  <span className="wiki-detail-label">Furnace Recipe</span>
                  <div className="wiki-detail-box wiki-furnace-wrap">
                    <div className="wiki-furnace-layout">
                      <div className="wiki-furnace-inputs">
                        <div className="wiki-furnace-slot" title={ingName}>
                          <McIcon materialKey={ingMat} size={32} />
                        </div>
                        <div className="wiki-furnace-fire">🔥</div>
                        <div className="wiki-furnace-slot wiki-furnace-slot--fuel" title={fuelName || 'Any fuel'}>
                          {fr.fuel ? <McIcon materialKey={fuelMat} size={32} /> : <span style={{ fontSize: 20 }}>🪵</span>}
                        </div>
                      </div>
                      <div className="wiki-furnace-arrow">▶</div>
                      <div className="wiki-furnace-output">
                        <div className="wiki-furnace-slot wiki-furnace-slot--out">
                          <McIcon materialKey={itemMaterial(item)} size={32} />
                          {(fr.output_count ?? 1) > 1 && <span className="wiki-furnace-count">×{fr.output_count}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="wiki-furnace-meta">
                      <span className="wiki-furnace-ing-label">
                        {ingItem
                          ? <Link to={`/wiki/item/${ingItem.slug}`} className="wiki-recipe-ing-link">{ingName}</Link>
                          : ingName}
                        {fuelName && <> + {fuelItem
                          ? <Link to={`/wiki/item/${fuelItem.slug}`} className="wiki-recipe-ing-link">{fuelName}</Link>
                          : fuelName}</>}
                      </span>
                      {fr.smelt_time && <span className="wiki-furnace-time">{fr.smelt_time}s smelt time</span>}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* ── Ways to Obtain ── */}
            {(() => {
              const entries = parseObtain(item.obtain);
              if (!entries.length) return null;
              return (
                <div>
                  <span className="wiki-detail-label">Ways to Obtain</span>
                  <div className="wiki-obtain-box">
                    {entries.map((entry, i) => (
                      <ObtainEntry key={i} entry={entry} mobs={mobs} material={itemMaterial(item)} />
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* ── Used to Craft ── */}
            {(() => {
              const usedIn = item.recipe_usage || [];
              if (!usedIn.length) return null;
              const hasAbilities = (item.abilities || []).length > 0;
              return (
                <div style={!hasAbilities ? {
                  background: 'linear-gradient(135deg, var(--bg-raised) 0%, var(--bg-card) 100%)',
                  border: '1px solid var(--teal)',
                  borderRadius: 'var(--r-md)',
                  padding: '1.2rem'
                } : undefined}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    <McIcon materialKey="crafting_table" size={20} />
                    <span className="wiki-detail-label" style={!hasAbilities ? { color: 'var(--teal)', margin: 0 } : { margin: 0 }}>Used to Craft</span>
                  </div>
                  <div className={hasAbilities ? "wiki-detail-box" : ""}>
                    {usedIn.map((output, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingBottom: '0.5rem', borderBottom: idx < usedIn.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <McIcon materialKey={output.material || 'barrier'} size={20} />
                        <Link to={`/wiki/item/${output.slug}`} className="wiki-obtain-mob-link" style={{ flex: 1, fontSize: 'var(--t-sm)' }}>
                          {output.name}
                        </Link>
                        {output.output_count > 1 && (
                          <span style={{ color: 'var(--text-muted)', fontSize: 'var(--t-xs)' }}>×{output.output_count}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* ── Part of Set ── */}
            {(() => {
              if (!item.set_id) return null;
              const set = sets.find(s => String(s.id) === String(item.set_id));
              if (!set) return null;
              const setColor = set.color || 'var(--teal)';
              return (
                <div>
                  <span className="wiki-detail-label">Part of a Set</span>
                  <div className="wiki-detail-box" style={{ borderColor: setColor + '55' }}>
                    <Link to={`/wiki/set/${set.slug}`} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, textDecoration: 'none' }}>
                      <div style={{ width: 36, height: 36, background: 'var(--bg-card)', border: `1px solid ${setColor}55`, borderRadius: 'var(--r-sm)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {set.icon
                          ? <McIcon materialKey={set.icon} size={24} />
                          : <span style={{ fontSize: '1rem', color: setColor }}>◈</span>
                        }
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, color: setColor, fontSize: 'var(--t-sm)', lineHeight: 1.2 }}>{set.name}</div>
                        <div style={{ fontSize: 'var(--t-xs)', color: 'var(--text-muted)' }}>View full set →</div>
                      </div>
                    </Link>
                    {set.description && (
                      <p style={{ margin: '0 0 10px', color: 'var(--text-secondary)', fontSize: 'var(--t-xs)', lineHeight: 1.5 }}>
                        {set.description}
                      </p>
                    )}
                    {(set.items || []).length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {set.items.map(si => (
                          <div key={si.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <McIcon materialKey={si.material || 'barrier'} size={16} />
                            {si.slug === item.slug ? (
                              <span style={{ fontSize: 'var(--t-xs)', color: setColor, fontWeight: 600 }}>{si.name}</span>
                            ) : (
                              <Link to={`/wiki/item/${si.slug}`} className="wiki-obtain-mob-link" style={{ fontSize: 'var(--t-xs)' }}>
                                {si.name}
                              </Link>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </StaggerItem>
        </div>

        {(() => {
          const history = parseHistory(item.history);
          if (!history.length) return null;
          return (
            <StaggerItem className="wiki-history-section" style={{ marginTop: '-1.5rem' }}>
              <h3 className="wiki-history-title">Updates</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {history.map((entry, idx) => (
                  <div key={idx} className="wiki-history-row">
                    <span className="wiki-history-version">{entry.version}</span>
                    <ul className="wiki-history-changes">
                      {(entry.changes ?? []).map((c, ci) => (
                        <li key={ci}>{c}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </StaggerItem>
          );
        })()}
      </StaggerContainer>
    </div>
  );
}
